import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Wallet, Plus, Pencil, Trash2, Check, X, Building2,
  ChevronDown, TrendingUp, TrendingDown, Minus, DollarSign,
  AlertCircle, CreditCard, ShieldCheck, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { useTenant } from '../contexts/TenantContext';
import BankIcon from '../components/BankIcon';
import { formatCurrency } from '../utils/format';

// ── Bancos predefinidos ───────────────────────────────────────────────────────
const BANCOS_PREDEFINIDOS = [
  { nome: 'Nubank',          cor: '#820AD1' },
  { nome: 'Itaú',            cor: '#FF6200' },
  { nome: 'Bradesco',        cor: '#CC0000' },
  { nome: 'Santander',       cor: '#EC0000' },
  { nome: 'Banco do Brasil', cor: '#F9C900' },
  { nome: 'Caixa',           cor: '#006BB7' },
  { nome: 'Inter',           cor: '#FF7A00' },
  { nome: 'C6 Bank',         cor: '#242424' },
  { nome: 'Cora',            cor: '#FF4785' },
  { nome: 'EFI Bank',        cor: '#00A651' },
  { nome: 'Sicoob',          cor: '#007A3D' },
  { nome: 'Sicredi',         cor: '#008542' },
  { nome: 'BTG Pactual',     cor: '#1C3D6E' },
  { nome: 'XP',              cor: '#1D1D1B' },
  { nome: 'Mercado Pago',    cor: '#009EE3' },
  { nome: 'PicPay',          cor: '#21C25E' },
  { nome: 'Outro',           cor: '#64748b' },
];

const PALETTE = [
  '#0147a0','#820AD1','#FF6200','#CC0000','#EC0000',
  '#F9C900','#006BB7','#FF7A00','#242424','#FF4785',
  '#00A651','#007A3D','#1C3D6E','#1D1D1B','#64748b',
  '#339933','#d72d21','#f97316','#8b5cf6','#06b6d4',
];

// ── Currency input helper ─────────────────────────────────────────────────────
function parseCurrencyInput(v) {
  if (!v) return 0;
  const s = String(v).replace(/[^\d,.]/g, '');
  if (s.includes(',')) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
  }
  return parseFloat(s) || 0;
}

function maskCurrencyInput(v) {
  return String(v).replace(/[^\d,.]/g, '');
}

// ── Modal de Criação / Edição de Carteira ──────────────────────────────────────
function CarteiraModal({ isOpen, initial, accent, onSave, onClose }) {
  const [nome, setNome]               = useState('');
  const [agencia, setAgencia]         = useState('');
  const [conta, setConta]             = useState('');
  const [tipo, setTipo]               = useState('PF');
  const [cor, setCor]                 = useState('#0147a0');
  const [saldoInicial, setSaldoInicial] = useState('');
  const [showBancos, setShowBancos]   = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    if (isOpen) {
      setNome(initial?.nome || '');
      setAgencia(initial?.agencia || '');
      setConta(initial?.conta || '');
      setTipo(initial?.tipo || 'PF');
      setCor(initial?.cor || accent || '#0147a0');
      setSaldoInicial(
        initial?.saldoInicial != null && initial.saldoInicial !== 0
          ? String(initial.saldoInicial).replace('.', ',')
          : ''
      );
      setShowBancos(false);
      setError('');
    }
  }, [isOpen, initial, accent]);

  if (!isOpen) return null;

  const selectBanco = (banco) => {
    setNome(banco.nome);
    setCor(banco.cor);
    setShowBancos(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!nome.trim()) {
      setError('Por favor, informe o nome ou banco da conta.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSave({
        nome: nome.trim(),
        agencia: agencia.trim(),
        conta: conta.trim(),
        tipo,
        cor,
        saldoInicial: parseCurrencyInput(saldoInicial),
      });
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erro ao salvar carteira.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="carteira-modal-overlay"
    >
      <div
        className="nexus-modal w-full max-w-lg flex flex-col my-auto max-h-[92vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="carteira-modal"
      >
        {/* Barra superior de destaque com a cor selecionada */}
        <div className="h-1.5 transition-colors duration-200" style={{ background: cor || accent }} />

        {/* Header do modal */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <BankIcon nome={nome} cor={cor || accent} size={36} />
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {initial ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {initial ? 'Atualize as informações da conta' : 'Cadastre sua conta ou carteira financeira'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            data-testid="carteira-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Conteúdo do formulário */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl text-xs font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
              <AlertCircle size={15} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Tipo de Conexão / Sincronização */}
          <div>
            <label className="fin-label text-xs">Tipo de Conexão</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div className="p-2.5 rounded-xl border-2 border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/40 text-xs font-bold text-indigo-700 dark:text-indigo-300 flex items-center justify-between">
                <span>Manual / Extrato</span>
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
              </div>
              <div
                className="p-2.5 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-800/40 text-xs font-semibold text-slate-400 flex items-center justify-between cursor-not-allowed select-none opacity-70"
                title="Sincronização bancária automática em fase de homologação"
              >
                <span>Automática (Open Finance)</span>
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500">
                  Em breve
                </span>
              </div>
            </div>
          </div>

          {/* Banco / Instituição */}
          <div>
            <label className="fin-label text-xs">Banco / Instituição *</label>
            <div className="relative mt-1">
              <button
                type="button"
                onClick={() => setShowBancos(v => !v)}
                className="fin-input flex items-center justify-between w-full text-left cursor-pointer"
              >
                <span className="flex items-center gap-2 truncate">
                  <BankIcon nome={nome} cor={cor} size={24} />
                  <span className={nome ? 'text-slate-800 dark:text-slate-200 font-medium' : 'text-slate-400'}>
                    {nome || 'Selecione uma instituição...'}
                  </span>
                </span>
                <ChevronDown size={15} className="text-slate-400 flex-shrink-0 ml-1" />
              </button>

              {showBancos && (
                <div className="absolute z-30 mt-1.5 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl max-h-52 overflow-y-auto p-1.5 space-y-0.5">
                  <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                    Instituições Populares
                  </div>
                  {BANCOS_PREDEFINIDOS.map(b => (
                    <button
                      key={b.nome}
                      type="button"
                      onClick={() => selectBanco(b)}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/60 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 transition-colors text-left"
                    >
                      <span className="flex items-center gap-2">
                        <BankIcon nome={b.nome} cor={b.cor} size={26} />
                        <span>{b.nome}</span>
                      </span>
                      {nome === b.nome && <Check size={13} className="text-emerald-500" strokeWidth={3} />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              className="fin-input mt-2 text-xs"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ou digite o nome personalizado da conta..."
              data-testid="carteira-nome-input"
            />
          </div>

          {/* Agência e Conta */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="fin-label text-xs">Agência</label>
              <input
                className="fin-input text-xs"
                value={agencia}
                onChange={e => setAgencia(e.target.value)}
                placeholder="Ex: 0001"
                data-testid="carteira-agencia-input"
              />
            </div>
            <div>
              <label className="fin-label text-xs">Conta / Dígito</label>
              <input
                className="fin-input text-xs"
                value={conta}
                onChange={e => setConta(e.target.value)}
                placeholder="Ex: 12345-6"
                data-testid="carteira-conta-input"
              />
            </div>
          </div>

          {/* Saldo Inicial */}
          <div className="bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/40 rounded-2xl p-3.5 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                <DollarSign size={14} className="text-emerald-600 dark:text-emerald-400" />
                Saldo Inicial da Conta
              </label>
              <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/60 px-2 py-0.5 rounded-full">
                Saldo de Abertura
              </span>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
              Informe o saldo que esta conta possui hoje. Os lançamentos futuros serão somados ou debitados automaticamente.
            </p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-xs">R$</span>
              <input
                className="fin-input pl-9 text-sm font-semibold text-slate-800 dark:text-slate-100"
                value={saldoInicial}
                onChange={e => setSaldoInicial(maskCurrencyInput(e.target.value))}
                placeholder="0,00"
                inputMode="decimal"
                data-testid="carteira-saldo-input"
              />
            </div>
          </div>

          {/* Tipo de Conta */}
          <div>
            <label className="fin-label text-xs">Tipo de Titularidade</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { id: 'PF', label: 'Pessoa Física', sub: 'Conta pessoal' },
                { id: 'PJ', label: 'Pessoa Jurídica', sub: 'Conta empresarial' }
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTipo(t.id)}
                  className={`p-2.5 rounded-xl border text-left transition-all ${
                    tipo === t.id
                      ? 'border-transparent shadow-sm text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                  }`}
                  style={tipo === t.id ? { background: cor || accent } : {}}
                >
                  <p className="text-xs font-bold leading-tight">{t.label}</p>
                  <p className={`text-[10px] mt-0.5 ${tipo === t.id ? 'opacity-80 text-white' : 'text-slate-400'}`}>
                    {t.sub}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Cor do Badge */}
          <div>
            <label className="fin-label text-xs">Cor de identificação</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {PALETTE.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCor(c)}
                  className="w-6 h-6 rounded-full transition-transform hover:scale-115 flex items-center justify-center relative cursor-pointer"
                  style={{
                    background: c,
                    outline: cor === c ? `2px solid ${c}` : 'none',
                    outlineOffset: '2px'
                  }}
                  title={c}
                >
                  {cor === c && <Check size={11} className="text-white" strokeWidth={3} />}
                </button>
              ))}
            </div>
          </div>
        </form>

        {/* Rodapé com botões de ação */}
        <div className="flex items-center justify-end gap-2.5 px-5 sm:px-6 py-3.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/50">
          <button
            type="button"
            onClick={onClose}
            className="fin-btn-secondary text-xs"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 shadow-sm"
            style={{ background: cor || accent }}
            data-testid="carteira-modal-submit"
          >
            <Check size={14} />
            {loading ? 'Salvando...' : initial ? 'Salvar alterações' : 'Cadastrar conta'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Saldo Inicial inline editor ───────────────────────────────────────────────
function SaldoInicialEditor({ carteira, onUpdated }) {
  const [editing, setEditing]   = useState(false);
  const [input, setInput]       = useState('');
  const [saving, setSaving]     = useState(false);

  const open = () => {
    setInput(
      carteira.saldoInicial != null && carteira.saldoInicial !== 0
        ? String(carteira.saldoInicial).replace('.', ',')
        : ''
    );
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/carteiras/${carteira.id}/saldo-inicial`, {
        saldoInicial: parseCurrencyInput(input),
      });
      setEditing(false);
      toast.success('Saldo inicial atualizado!');
      onUpdated();
    } catch (err) {
      toast.error('Erro ao atualizar saldo inicial.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={open}
        className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors group cursor-pointer"
        title="Clique para editar o saldo inicial"
      >
        <span>
          Inicial: <span className="font-semibold text-slate-700 dark:text-slate-300">
            {carteira.saldoInicial ? formatCurrency(carteira.saldoInicial) : 'R$ 0,00'}
          </span>
        </span>
        <Pencil size={10} className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition-opacity" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 mt-1">
      <span className="text-[10px] text-slate-400 font-semibold">R$</span>
      <input
        autoFocus
        className="w-20 text-[11px] font-semibold border border-slate-300 dark:border-slate-600 rounded-lg px-1.5 py-0.5 bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        value={input}
        onChange={e => setInput(maskCurrencyInput(e.target.value))}
        placeholder="0,00"
        inputMode="decimal"
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
      />
      <button
        onClick={save}
        disabled={saving}
        className="p-1 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer"
        title="Confirmar"
      >
        <Check size={10} strokeWidth={3} />
      </button>
      <button
        onClick={() => setEditing(false)}
        className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors cursor-pointer"
        title="Cancelar"
      >
        <X size={10} />
      </button>
    </div>
  );
}

// ── Saldo Atual inline editor ─────────────────────────────────────────────────
// Define o saldo real que está na conta; o sistema recalcula o saldo inicial para fechar a conta.
function SaldoAtualEditor({ carteira, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput]     = useState('');
  const [saving, setSaving]   = useState(false);
  const saldo = carteira.saldo ?? 0;

  const open = () => {
    setInput(String(saldo).replace('.', ','));
    setEditing(true);
  };

  const save = async () => {
    const negativo = input.trim().startsWith('-');
    const valor = parseCurrencyInput(input) * (negativo ? -1 : 1);
    setSaving(true);
    try {
      await api.patch(`/carteiras/${carteira.id}/saldo-atual`, { saldoAtual: valor });
      setEditing(false);
      toast.success('Saldo atual atualizado!');
      onUpdated();
    } catch (err) {
      toast.error('Erro ao atualizar o saldo atual.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={open}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer"
        title="Informar o saldo real que está na sua conta"
      >
        <Pencil size={10} /> Ajustar saldo atual
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 mt-1">
      <span className="text-[10px] text-slate-400 font-semibold">R$</span>
      <input
        autoFocus
        className="w-24 text-[11px] font-semibold border border-slate-300 dark:border-slate-600 rounded-lg px-1.5 py-0.5 bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        value={input}
        onChange={e => setInput(e.target.value.replace(/[^\d,.-]/g, ''))}
        placeholder="0,00"
        inputMode="decimal"
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
      />
      <button onClick={save} disabled={saving} className="p-1 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer" title="Confirmar">
        <Check size={10} strokeWidth={3} />
      </button>
      <button onClick={() => setEditing(false)} className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors cursor-pointer" title="Cancelar">
        <X size={10} />
      </button>
    </div>
  );
}

// ── Card de Carteira (Compacto e Elegante) ────────────────────────────────────
function CarteiraCard({ carteira, onEdit, onDelete, onSaldoUpdated }) {
  const saldo      = carteira.saldo ?? 0;
  const saldoColor = saldo > 0 ? '#16a34a' : saldo < 0 ? '#dc2626' : '#64748b';
  const SaldoIcon  = saldo > 0 ? TrendingUp : saldo < 0 ? TrendingDown : Minus;

  return (
    <div className="fin-card relative overflow-hidden flex flex-col justify-between transition-all hover:shadow-md border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-800/90 rounded-2xl group">
      {/* Faixa superior de cor personalizada */}
      <div className="h-1.5 w-full" style={{ background: carteira.cor || '#0147a0' }} />

      <div className="p-4 sm:p-4.5 flex-1 flex flex-col justify-between space-y-3.5">
        {/* Topo do card: Banco, chip e ações */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <BankIcon nome={carteira.nome} cor={carteira.cor} size={40} className="shadow-sm" />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100 truncate" title={carteira.nome}>
                  {carteira.nome}
                </h3>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/80 flex-shrink-0">
                  {carteira.tipo === 'PJ' ? 'PJ' : 'PF'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-400 dark:text-slate-400 mt-0.5">
                {carteira.agencia && <span>Ag: {carteira.agencia}</span>}
                {carteira.agencia && carteira.conta && <span>•</span>}
                {carteira.conta && <span>CC: {carteira.conta}</span>}
                {!carteira.agencia && !carteira.conta && <span className="italic">Sem agência/conta</span>}
              </div>
            </div>
          </div>

          {/* Botões discretos de ação */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onEdit(carteira)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
              title="Editar conta"
              data-testid={`edit-carteira-${carteira.id}`}
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => onDelete(carteira)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors cursor-pointer"
              title="Excluir conta"
              data-testid={`delete-carteira-${carteira.id}`}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>

        {/* Meio: Saldo Atual em destaque */}
        <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60 flex items-end justify-between gap-2">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400">
              Saldo Atual
            </span>
            <div className="flex items-center gap-1 mt-0.5">
              <SaldoIcon size={14} style={{ color: saldoColor }} />
              <span className="text-lg font-bold tracking-tight" style={{ color: saldoColor, fontFamily: 'Manrope, sans-serif' }}>
                {saldo < 0 ? '-' : ''}{formatCurrency(Math.abs(saldo))}
              </span>
            </div>
            <SaldoAtualEditor carteira={carteira} onUpdated={onSaldoUpdated} />
          </div>

          <div className="text-right">
            <SaldoInicialEditor carteira={carteira} onUpdated={onSaldoUpdated} />
          </div>
        </div>
      </div>

      {/* Rodapé sutil com resumo */}
      <div className="px-4 py-2 bg-slate-50/70 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400 dark:text-slate-400 flex items-center justify-between">
        <span>Movimentações integradas</span>
        <span className="font-medium text-slate-500 dark:text-slate-300">
          Inicial: {formatCurrency(carteira.saldoInicial || 0)}
        </span>
      </div>
    </div>
  );
}

// ── Página Principal da Carteira ──────────────────────────────────────────────
export default function Carteira() {
  const { tenant } = useTenant();
  const accent = tenant?.corPrimaria || '#0147a0';

  const [carteiras, setCarteiras]             = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [modalOpen, setModalOpen]             = useState(false);
  const [editingCarteira, setEditingCarteira] = useState(null);
  const [deletingCarteira, setDeletingCarteira] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/carteiras/saldos')
      .then(r => setCarteiras(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    if (editingCarteira) {
      await api.put(`/carteiras/${editingCarteira.id}`, data);
      toast.success('Conta atualizada com sucesso!');
    } else {
      await api.post('/carteiras', data);
      toast.success('Conta cadastrada com sucesso!');
    }
    setEditingCarteira(null);
    setModalOpen(false);
    load();
  };

  const confirmDelete = async () => {
    if (!deletingCarteira) return;
    try {
      await api.delete(`/carteiras/${deletingCarteira.id}`);
      toast.success('Conta removida com sucesso!');
      setDeletingCarteira(null);
      load();
    } catch {
      toast.error('Erro ao remover conta.');
    }
  };

  // Totais consolidados
  const totalSaldo = carteiras.reduce((acc, c) => acc + (c.saldo || 0), 0);
  const totalInicial = carteiras.reduce((acc, c) => acc + (c.saldoInicial || 0), 0);

  return (
    <div className="w-full max-w-5xl mx-auto space-y-5 page-enter pb-14 px-2 sm:px-4">

      {/* Header Compacto e Harmonioso */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
        <div>
          <h1
            className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            <Wallet size={20} style={{ color: accent }} />
            Carteira & Contas Bancárias
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Gerencie suas contas financeiras. O saldo reflete o saldo inicial mais todas as transações computadas.
          </p>
        </div>
        <button
          onClick={() => { setEditingCarteira(null); setModalOpen(true); }}
          className="inline-flex items-center justify-center gap-1.5 text-white text-xs sm:text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:opacity-90 active:scale-95 shadow-sm flex-shrink-0 cursor-pointer"
          style={{ background: accent }}
          data-testid="nova-conta-btn"
        >
          <Plus size={15} /> Nova Conta
        </button>
      </div>

      {/* Card de Resumo Consolidado (Compacto e Sofisticado) */}
      {carteiras.length > 0 && (
        <div
          className="rounded-2xl p-4 sm:p-5 text-white shadow-md relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)` }}
        >
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider opacity-85">
                  Saldo Total Consolidado
                </span>
                <span className="text-[10px] font-bold bg-white/20 px-2 py-0.5 rounded-full">
                  {carteiras.length} conta{carteiras.length !== 1 ? 's' : ''} ativa{carteiras.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold mt-1 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {totalSaldo < 0 ? '-' : ''}{formatCurrency(Math.abs(totalSaldo))}
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs opacity-90 sm:border-l sm:border-white/20 sm:pl-4">
              <div>
                <span className="block text-[11px] opacity-75">Saldo Inicial Total</span>
                <span className="font-bold text-sm">{formatCurrency(totalInicial)}</span>
              </div>
              <div>
                <span className="block text-[11px] opacity-75">Status Geral</span>
                <span className={`font-bold text-sm ${totalSaldo >= 0 ? 'text-emerald-200' : 'text-rose-200'}`}>
                  {totalSaldo >= 0 ? 'Positivo' : 'Negativo'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Contas em Grid Responsivo */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Carregando carteiras...</div>
      ) : carteiras.length === 0 ? (
        <div className="text-center py-12 px-4 fin-card max-w-lg mx-auto rounded-2xl border border-slate-200/80 dark:border-slate-800">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
            style={{ background: `${accent}18` }}
          >
            <Wallet size={24} style={{ color: accent }} />
          </div>
          <h3 className="font-bold text-slate-700 dark:text-slate-200 text-sm mb-1">
            Nenhuma conta cadastrada
          </h3>
          <p className="text-xs text-slate-400 max-w-xs mx-auto mb-4">
            Cadastre suas contas bancárias (Nubank, Itaú, Inter, etc.) para organizar suas receitas e despesas.
          </p>
          <button
            onClick={() => { setEditingCarteira(null); setModalOpen(true); }}
            className="inline-flex items-center gap-1.5 text-white text-xs font-semibold px-4 py-2 rounded-xl hover:opacity-90 transition-opacity cursor-pointer shadow-sm"
            style={{ background: accent }}
          >
            <Plus size={14} /> Cadastrar primeira conta
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {carteiras.map(c => (
            <CarteiraCard
              key={c.id}
              carteira={c}
              onEdit={crt => {
                setEditingCarteira(crt);
                setModalOpen(true);
              }}
              onDelete={setDeletingCarteira}
              onSaldoUpdated={load}
            />
          ))}
        </div>
      )}

      {/* Modal de Criação / Edição */}
      <CarteiraModal
        isOpen={modalOpen}
        initial={editingCarteira}
        accent={accent}
        onSave={handleSave}
        onClose={() => {
          setModalOpen(false);
          setEditingCarteira(null);
        }}
      />

      {/* Modal de Confirmação de Exclusão */}
      {deletingCarteira && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setDeletingCarteira(null)}
        >
          <div
            className="nexus-modal p-5 sm:p-6 w-full max-w-sm rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-500 flex items-center justify-center mb-3">
              <Trash2 size={20} />
            </div>
            <h3
              className="text-base font-bold text-slate-800 dark:text-slate-100 mb-1.5"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Remover conta bancária
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-5 leading-relaxed">
              Tem certeza que deseja remover <strong>"{deletingCarteira.nome}"</strong>?
              Os lançamentos vinculados não serão apagados, mas perderão a associação com esta conta.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeletingCarteira(null)}
                className="fin-btn-secondary text-xs"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="inline-flex items-center gap-1.5 text-white text-xs font-semibold px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 transition-colors shadow-sm cursor-pointer"
              >
                <Trash2 size={13} /> Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
